/**
 * RecordPaymentFlow — the staff/admin 3-step record-payment modal (money
 * module SLICE 2). Pick gate → Payment details → Confirm & release. Drives
 * the PURE `recordPayment` decisions and the deployed `markPaidManual`
 * callable (via `recordPaymentStore`). Inherits the reskin tokens (green/
 * white/ink) — no literal hex except the mockup's USD slate-blue identity.
 *
 * It NEVER flips a gate itself — a clearing payment goes through
 * `markPaidManual` (the SOLE money authority); a partial writes a ledger-only
 * `payments/` doc. Partial / full / override-settlement all handled.
 *
 * After-save: confirmation recap + updated statement row. Receipt SENDING
 * (WhatsApp / email) is NOT built yet, so no send controls render — owner hard
 * rule 2026-07-03: no dead controls (the old stubbed channel cards were
 * removed). One muted line states receipts are manual until the send
 * transport lands.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { Btn3 } from './ui';
import CreateProjectFromPaymentPanel from './CreateProjectFromPaymentPanel';
import { money, inr, usd } from '../lib/moneyModel';
import {
  loadChasingByGate,
  loadPriorPayments,
  submitPayment,
  type SubmitResult,
} from '../lib/recordPaymentStore';
import {
  effectiveRate,
  gateSelectable,
  methodLabel,
  methodsFor,
  payableGates,
  planPayment,
  type PayContextProject,
  type PayableGate,
  type UiMethod,
} from '../lib/recordPayment';
import type { Currency } from '../lib/firms';
import type { LiveProject } from '../lib/projectStore';
import { createPaypalLink } from '../lib/paypalPayment';

const USD_ACCENT = '#2f5d8a'; // mockup USD identity (slate-blue, AV accent)

/** Adapt a LiveProject into the pure `PayContextProject`. */
function toPayCtx(p: LiveProject): PayContextProject {
  return {
    pid: p.pid,
    vp: p.vp,
    clientName: p.clientName,
    clientNo: p.clientNo,
    clientId: p.clientId,
    projectName: p.projectName,
    tierCode: p.tierCode,
    valueINR: p.valueINR,
    currency: p.currency,
    moneyPaid: p.moneyPaid,
    // AGREED-PRICE — carry the seeded per-gate override into the modal so the
    // canonical resolver expects the agreed $40, not the template ₹11,000.
    // (This single missing line was the Record-a-payment screenshot bug.)
    gateAmounts: p.gateAmounts,
  };
}

type Step = 'pick' | 'details' | 'done';

export default function RecordPaymentFlow({
  projects,
  initialProjectId,
  onClose,
}: {
  /** All live projects (the picker scopes to those with priced money gates). */
  projects: LiveProject[];
  /** When opened from a project row, pre-select it and skip client search. */
  initialProjectId?: string;
  onClose: () => void;
}) {
  const { staffId, user } = useAuth();
  const actorName =
    user?.displayName || user?.email || 'Aayatan Veda team';

  const [step, setStep] = useState<Step>('pick');
  const [projectId, setProjectId] = useState<string | null>(
    initialProjectId ?? null,
  );
  const [gateId, setGateId] = useState<string | null>(null);
  const [prior, setPrior] = useState<
    { gateId: string; amount: number; partial: boolean }[] | null
  >(null);
  const [chasing, setChasing] = useState<{
    chasingByGate: Record<string, number>;
    reminderIdByGate: Record<string, string>;
  }>({ chasingByGate: {}, reminderIdByGate: {} });
  const [search, setSearch] = useState('');

  // payment-detail form state
  const [mode, setMode] = useState<'full' | 'partial'>('full');
  const [amountStr, setAmountStr] = useState('');
  const [uiMethod, setUiMethod] = useState<UiMethod | null>(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  // MULTI-CURRENCY: the currency the client actually PAID in (defaults to the
  // project currency) + the INR the bank credited for a USD receipt.
  const [recvCurrency, setRecvCurrency] = useState<Currency>('INR');
  const [settledStr, setSettledStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const project = useMemo(
    () => projects.find((p) => p.pid === projectId) ?? null,
    [projects, projectId],
  );

  // Load the project's prior payments (running-balance math) on select.
  useEffect(() => {
    if (!projectId) {
      setPrior(null);
      return;
    }
    let live = true;
    setPrior(null);
    setChasing({ chasingByGate: {}, reminderIdByGate: {} });
    Promise.all([
      loadPriorPayments(projectId),
      loadChasingByGate(projectId).catch(() => ({
        chasingByGate: {},
        reminderIdByGate: {},
      })),
    ])
      .then(([rows, chase]) => {
        if (!live) return;
        setPrior(rows);
        setChasing(chase);
      })
      .catch(() => {
        if (live) setPrior([]); // a read failure → treat as no priors
      });
    return () => {
      live = false;
    };
  }, [projectId]);

  const gates: PayableGate[] = useMemo(() => {
    if (!project || prior === null) return [];
    return payableGates(toPayCtx(project), prior, chasing.chasingByGate);
  }, [project, prior, chasing]);

  const gate = useMemo(
    () => gates.find((g) => g.id === gateId) ?? null,
    [gates, gateId],
  );

  // When a gate is chosen, prefill the full-payment amount to its balance.
  useEffect(() => {
    if (gate && mode === 'full') setAmountStr(String(gate.balance));
  }, [gate, mode]);

  // Received currency defaults to the project currency when a project is
  // chosen; the staff can override it (e.g. a USD engagement paid USD →
  // INR-settled). Reset the bank-credit field whenever the project changes.
  useEffect(() => {
    if (project) {
      setRecvCurrency(project.currency);
      setSettledStr('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.pid]);

  // Methods follow the RECEIVED currency (USD rails for a USD receipt).
  const methods = methodsFor(recvCurrency);
  useEffect(() => {
    if (!uiMethod || !methods.includes(uiMethod)) {
      setUiMethod(methods[0] ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recvCurrency]);

  const amount = Number(amountStr.replace(/[^0-9.]/g, '')) || 0;
  const settledINR = Number(settledStr.replace(/[^0-9.]/g, '')) || 0;
  const isUsdReceipt = recvCurrency === 'USD';
  const fxRate = isUsdReceipt ? effectiveRate(amount, settledINR) : null;
  const plan = gate ? planPayment({ gate, amount, mode }) : null;

  /** Format an amount in the RECEIVED currency (the gate/payment unit). */
  function fmt(n: number) {
    return money(n, recvCurrency);
  }

  async function doSubmit() {
    if (!project || !gate || !uiMethod || !plan || plan.error) return;
    setSubmitting(true);
    setErr(null);
    try {
      const ref = [reference.trim(), note.trim() && `note: ${note.trim()}`]
        .filter(Boolean)
        .join(' · ');
      const res = await submitPayment({
        project: toPayCtx(project),
        gate,
        amount,
        mode,
        uiMethod,
        reference: ref,
        actorUid: staffId ?? 'system',
        actorName,
        currencyReceived: recvCurrency,
        settledINR: isUsdReceipt && settledINR > 0 ? settledINR : null,
        reminderId: gate.overrideOpen
          ? chasing.reminderIdByGate[gate.id]
          : undefined,
      });
      setResult(res);
      setStep('done');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not record the payment.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={ovl} role="dialog" aria-modal="true" aria-label="Record a payment">
      <div style={sheet}>
        {/* header */}
        <div style={head}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
              Payments · Record a payment
            </div>
            <div style={{ fontSize: 19, fontWeight: 700, marginTop: 2 }}>
              {step === 'done' ? 'Payment recorded' : 'Record a payment'}
            </div>
          </div>
          <button style={xbtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* stepper */}
        {step !== 'done' && (
          <Stepper active={step} hasGate={!!gateId} />
        )}

        <div style={body}>
          {step === 'pick' && (
            <PickStep
              projects={projects}
              project={project}
              gates={gates}
              gateId={gateId}
              prior={prior}
              search={search}
              setSearch={setSearch}
              onPickProject={(pid) => {
                setProjectId(pid);
                setGateId(null);
              }}
              onPickGate={setGateId}
              fmt={fmt}
            />
          )}

          {step === 'details' && project && gate && (
            <DetailsStep
              project={project}
              gate={gate}
              mode={mode}
              setMode={setMode}
              amountStr={amountStr}
              setAmountStr={setAmountStr}
              methods={methods}
              uiMethod={uiMethod}
              setUiMethod={setUiMethod}
              reference={reference}
              setReference={setReference}
              note={note}
              setNote={setNote}
              plan={plan}
              fmt={fmt}
              recvCurrency={recvCurrency}
              setRecvCurrency={setRecvCurrency}
              settledStr={settledStr}
              setSettledStr={setSettledStr}
              settledINR={settledINR}
              fxRate={fxRate}
            />
          )}

          {step === 'done' && result && project && gate && (
            <DoneStep
              project={project}
              gate={gate}
              amount={amount}
              result={result}
              fmt={fmt}
            />
          )}

          {err && (
            <div style={errBox}>{err}</div>
          )}
        </div>

        {/* footer actions */}
        <div style={foot}>
          {step === 'pick' && (
            <>
              <Btn3 intent="ghost" onClick={onClose}>
                Cancel
              </Btn3>
              <Btn3
                intent="ink"
                disabled={!gate || !gateSelectable(gate)}
                onClick={() => setStep('details')}
              >
                Continue to details →
              </Btn3>
            </>
          )}
          {step === 'details' && (
            <>
              <Btn3 intent="ghost" onClick={() => setStep('pick')}>
                ← Back
              </Btn3>
              <Btn3
                intent="grn"
                disabled={!plan || !!plan.error || !uiMethod || submitting}
                onClick={doSubmit}
              >
                {submitting
                  ? 'Recording…'
                  : plan?.clearsGate
                    ? 'Record & release →'
                    : 'Record part-payment →'}
              </Btn3>
            </>
          )}
          {step === 'done' && (
            <>
              <Btn3
                intent="ghost"
                onClick={() => {
                  // record another — reset to pick, keep the project
                  setStep('pick');
                  setGateId(null);
                  setAmountStr('');
                  setReference('');
                  setNote('');
                  setResult(null);
                  // refresh priors so the running balance reflects the write
                  if (projectId)
                    loadPriorPayments(projectId).then(setPrior).catch(() => {});
                }}
              >
                + Record another
              </Btn3>
              <Btn3 intent="ink" onClick={onClose}>
                Done
              </Btn3>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ Stepper ════════════════════ */

function Stepper({ active, hasGate }: { active: Step; hasGate: boolean }) {
  const steps: { key: Step; n: number; label: string }[] = [
    { key: 'pick', n: 1, label: 'Pick gate' },
    { key: 'details', n: 2, label: 'Payment details' },
  ];
  const idx = active === 'details' ? 1 : 0;
  return (
    <div style={stepWrap}>
      {steps.map((s, i) => {
        const done = i < idx || (s.key === 'pick' && hasGate && active !== 'pick');
        const on = i === idx;
        return (
          <span key={s.key} style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                border: `1.5px solid ${
                  on ? 'var(--ink)' : done ? 'var(--accent)' : 'var(--rule-strong)'
                }`,
                background: on
                  ? 'var(--ink)'
                  : done
                    ? 'var(--accent)'
                    : '#fff',
                color: on || done ? '#fff' : 'var(--ink-mute)',
              }}
            >
              {done ? '✓' : s.n}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                margin: '0 14px 0 8px',
                color: on ? 'var(--ink)' : 'var(--ink-mute)',
              }}
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span
                style={{
                  width: 36,
                  height: 2,
                  background: done ? 'var(--accent)' : 'var(--rule)',
                  marginRight: 14,
                }}
              />
            )}
          </span>
        );
      })}
    </div>
  );
}

/* ════════════════════ Step 1 — Pick ════════════════════ */

function PickStep({
  projects,
  project,
  gates,
  gateId,
  prior,
  search,
  setSearch,
  onPickProject,
  onPickGate,
  fmt,
}: {
  projects: LiveProject[];
  project: LiveProject | null;
  gates: PayableGate[];
  gateId: string | null;
  prior: unknown[] | null;
  search: string;
  setSearch: (s: string) => void;
  onPickProject: (pid: string) => void;
  onPickGate: (id: string) => void;
  fmt: (n: number) => string;
}) {
  const q = search.trim().toLowerCase();
  const matches = projects.filter((p) => {
    if (!q) return true;
    return (
      p.clientName.toLowerCase().includes(q) ||
      p.clientNo.toLowerCase().includes(q) ||
      p.vp.toLowerCase().includes(q) ||
      (p.projectName || '').toLowerCase().includes(q)
    );
  });

  // AD-031 — "Create project from this payment". Surface when the client being
  // paid has no delivery project yet (e.g. paid off a quote / scoping).
  const [creating, setCreating] = useState(false);

  if (creating) {
    return (
      <CreateProjectFromPaymentPanel onClose={() => setCreating(false)} />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={lbl}>Client · project</label>
        <input
          style={input}
          placeholder="Search by client name, C-number, VP, or project…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={pickerList}>
          {matches.length === 0 && (
            <div style={{ padding: 14, color: 'var(--ink-mute)', fontSize: 13 }}>
              No matching project.
            </div>
          )}
          {matches.slice(0, 40).map((p) => {
            const on = project?.pid === p.pid;
            return (
              <button
                key={p.pid}
                style={{ ...popt, ...(on ? poptOn : null) }}
                onClick={() => onPickProject(p.pid)}
              >
                <span style={{ fontWeight: 600 }}>
                  {p.vp} — {p.clientName}
                </span>
                <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                  {p.clientNo} · {p.tier}
                </span>
                <CurChip currency={p.currency} />
              </button>
            );
          })}
        </div>
        {/* AD-031 — no project for this client yet? Create one from the payment. */}
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-mute)' }}>
          Can’t find the project?{' '}
          <button
            type="button"
            onClick={() => setCreating(true)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: 'var(--accent, #008069)',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            Create a project from this payment →
          </button>
        </div>
      </div>

      {project && (
        <div>
          <label style={lbl}>Milestone money-gate</label>
          {prior === null ? (
            <div style={{ padding: 12, color: 'var(--ink-mute)', fontSize: 13 }}>
              Resolving this project's gates…
            </div>
          ) : gates.length === 0 ? (
            <div style={{ ...noteBox }}>
              This project has no priced money gates yet. Set its tier &amp;
              value (Payments → set up) and its payment schedule appears here.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gates.map((g) => (
                <GateRow
                  key={g.id}
                  g={g}
                  on={gateId === g.id}
                  onPick={() => gateSelectable(g) && onPickGate(g.id)}
                  fmt={fmt}
                />
              ))}
              <div style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                The selected gate's expected amount carries into the next step.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GateRow({
  g,
  on,
  onPick,
  fmt,
}: {
  g: PayableGate;
  on: boolean;
  onPick: () => void;
  fmt: (n: number) => string;
}) {
  const sel = gateSelectable(g);
  const tone =
    g.state === 'paid'
      ? { bg: 'var(--good-wash)', fg: 'var(--good)', tag: 'Paid' }
      : g.state === 'override'
        ? { bg: '#f0ecf8', fg: '#6b4ea3', tag: 'Released unpaid · settle' }
        : g.state === 'partial'
          ? { bg: 'var(--warn-wash)', fg: 'var(--warn)', tag: 'Partly paid' }
          : { bg: 'var(--warn-wash)', fg: 'var(--warn)', tag: 'Due · blocks delivery' };
  return (
    <button
      onClick={onPick}
      disabled={!sel}
      style={{
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 13px',
        borderRadius: 'var(--r-sm)',
        border: `1px solid ${on ? 'var(--accent)' : 'var(--rule-strong)'}`,
        outline: on ? '2px solid var(--accent)' : 'none',
        outlineOffset: 1,
        background: tone.bg,
        cursor: sel ? 'pointer' : 'not-allowed',
        opacity: sel ? 1 : 0.65,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 700, fontSize: 13.5 }}>{g.label}</span>
        <span
          style={{
            display: 'block',
            fontSize: 11.5,
            color: tone.fg,
            fontWeight: 600,
            marginTop: 2,
          }}
        >
          {tone.tag}
          {g.state === 'partial' &&
            ` · ${fmt(g.paidSoFar)} of ${fmt(g.expected)}`}
        </span>
      </span>
      <span style={{ textAlign: 'right' }}>
        <span style={{ fontWeight: 700, color: tone.fg, fontVariantNumeric: 'tabular-nums' }}>
          {fmt(g.state === 'paid' ? g.expected : g.balance)}
        </span>
        <span style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-mute)' }}>
          {g.state === 'paid' ? 'collected' : 'due'}
        </span>
      </span>
    </button>
  );
}

/* ════════════════════ Step 2 — Details ════════════════════ */

function DetailsStep({
  project,
  gate,
  mode,
  setMode,
  amountStr,
  setAmountStr,
  methods,
  uiMethod,
  setUiMethod,
  reference,
  setReference,
  note,
  setNote,
  plan,
  fmt,
  recvCurrency,
  setRecvCurrency,
  settledStr,
  setSettledStr,
  settledINR,
  fxRate,
}: {
  project: LiveProject;
  gate: PayableGate;
  mode: 'full' | 'partial';
  setMode: (m: 'full' | 'partial') => void;
  amountStr: string;
  setAmountStr: (s: string) => void;
  methods: UiMethod[];
  uiMethod: UiMethod | null;
  setUiMethod: (m: UiMethod) => void;
  reference: string;
  setReference: (s: string) => void;
  note: string;
  setNote: (s: string) => void;
  plan: ReturnType<typeof planPayment> | null;
  fmt: (n: number) => string;
  recvCurrency: Currency;
  setRecvCurrency: (c: Currency) => void;
  settledStr: string;
  setSettledStr: (s: string) => void;
  settledINR: number;
  fxRate: number | null;
}) {
  const amount = Number(amountStr.replace(/[^0-9.]/g, '')) || 0;
  const isUsdReceipt = recvCurrency === 'USD';

  // ── PayPal Tier B (USD auto-reconcile) — thin "generate link" hook. ──
  // Shows only for a USD PayPal receipt. Calls paypalCreateOrder (CF) for the
  // entered amount and surfaces the approve link to copy + send. The gate is
  // released SERVER-side when the client pays (capture/webhook → clearGatePaid)
  // — this button never touches gateState. The full multi-method Payment
  // Details Sender composer is a separate future build.
  const [ppBusy, setPpBusy] = useState(false);
  const [ppLink, setPpLink] = useState<string | null>(null);
  const [ppErr, setPpErr] = useState<string | null>(null);
  const [ppCopied, setPpCopied] = useState(false);

  async function genPaypalLink() {
    setPpBusy(true);
    setPpErr(null);
    setPpLink(null);
    setPpCopied(false);
    try {
      const res = await createPaypalLink({
        projectId: project.pid,
        gateId: gate.id,
        amountUSD: amount,
      });
      setPpLink(res.approveUrl);
    } catch (e) {
      setPpErr(
        e instanceof Error ? e.message : 'Could not create the PayPal link.',
      );
    } finally {
      setPpBusy(false);
    }
  }

  async function copyPaypalLink() {
    if (!ppLink) return;
    try {
      await navigator.clipboard.writeText(ppLink);
      setPpCopied(true);
      setTimeout(() => setPpCopied(false), 2000);
    } catch {
      /* clipboard blocked — the link is still selectable in the field */
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* chosen context */}
      <div style={chosen}>
        <b>{project.clientName}</b>
        <span style={{ color: 'var(--rule-strong)' }}>›</span>
        <b>{project.vp}</b>
        <span style={{ color: 'var(--rule-strong)' }}>›</span>
        <b>{gate.label}</b>
        <CurChip currency={recvCurrency} />
        {gate.overrideOpen && (
          <span style={{ ...pill, background: '#f0ecf8', color: '#6b4ea3' }}>
            override · settle
          </span>
        )}
      </div>

      {/* gate expected */}
      <div style={{ ...gateBox }}>
        <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>
          {gate.overrideOpen
            ? 'Released unpaid — chasing balance'
            : 'Gate balance outstanding'}
        </span>
        <span style={{ fontWeight: 700, fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>
          {fmt(gate.balance)}
        </span>
      </div>

      {/* full / partial toggle (override gate forces the balance figure) */}
      {!gate.overrideOpen && (
        <div style={seg}>
          <button
            style={{ ...segBtn, ...(mode === 'full' ? segOn : null) }}
            onClick={() => {
              setMode('full');
              setAmountStr(String(gate.balance));
            }}
          >
            Full · {fmt(gate.balance)}
          </button>
          <button
            style={{ ...segBtn, ...(mode === 'partial' ? segOn : null) }}
            onClick={() => setMode('partial')}
          >
            Partial payment
          </button>
        </div>
      )}

      {/* amount + received currency (the client may pay USD even on a
          USD-priced engagement; the currency drives the method rails) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>Amount received *</label>
          <div style={amtCtrl}>
            <span style={{ color: 'var(--ink-mute)', fontWeight: 700 }}>
              {isUsdReceipt ? '$' : '₹'}
            </span>
            <input
              aria-label="Amount received"
              style={amtInput}
              inputMode="decimal"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              placeholder="0"
            />
          </div>
        </div>
        <div>
          <label style={lbl}>Received in *</label>
          <div style={seg}>
            {(['INR', 'USD'] as Currency[]).map((c) => (
              <button
                key={c}
                style={{ ...segBtn, ...(recvCurrency === c ? segOn : null) }}
                onClick={() => setRecvCurrency(c)}
              >
                {c === 'USD' ? '$ USD' : '₹ INR'}
              </button>
            ))}
          </div>
          {recvCurrency !== project.currency && (
            <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 5 }}>
              Engagement is priced in {project.currency}.
            </div>
          )}
        </div>
      </div>

      {/* MULTI-CURRENCY SETTLEMENT — USD received, INR credited at the bank.
          The USD amount clears the gate; the INR credited is recorded
          ALONGSIDE (banking fact) and the effective rate is DERIVED. */}
      {isUsdReceipt && (
        <div style={fxBox}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: USD_ACCENT }}>
            Bank settlement (forex)
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
            The client paid in USD; the bank credits INR after conversion.
            Enter the INR actually credited — the effective rate is computed
            (bank/forex fees are absorbed into it). The gate still clears on
            the USD amount above.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>INR credited to bank</label>
              <div style={amtCtrl}>
                <span style={{ color: 'var(--ink-mute)', fontWeight: 700 }}>₹</span>
                <input
                  style={amtInput}
                  inputMode="decimal"
                  value={settledStr}
                  onChange={(e) => setSettledStr(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <label style={lbl}>Effective rate</label>
              <div
                style={{
                  ...input,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 700,
                  color: fxRate != null ? USD_ACCENT : 'var(--ink-mute)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fxRate != null ? `₹${fxRate.toFixed(2)} / $` : '—'}
              </div>
            </div>
          </div>
          {amount > 0 && settledINR > 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
              {usd(amount)} received → {inr(settledINR)} banked
              {fxRate != null && ` · @ ₹${fxRate.toFixed(2)}/$`}
            </div>
          )}
          {amount > 0 && settledINR === 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--warn)' }}>
              No INR credit entered yet — the payment records with the USD amount
              only; add the bank credit when it lands to compute the rate.
            </div>
          )}
        </div>
      )}

      {/* running balance preview (in the GATE/received currency) */}
      {plan && !plan.error && amount > 0 && (
        <div style={balPrev}>
          <Row l="Gate balance" v={fmt(gate.balance)} />
          <Row l="This payment" v={`+ ${fmt(amount)}`} good />
          <Row
            l="Balance on this gate"
            v={
              plan.clearsGate
                ? `${fmt(0)} · clears ✓`
                : `${fmt(plan.balanceAfter)} remaining`
            }
            highlight={plan.clearsGate ? 'good' : 'warn'}
          />
        </div>
      )}

      {/* method (rails follow the RECEIVED currency) */}
      <div>
        <label style={lbl}>
          Method * <span style={{ fontWeight: 400, color: 'var(--ink-mute)' }}>
            — {recvCurrency} methods
          </span>
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {methods.map((m) => (
            <button
              key={m}
              onClick={() => setUiMethod(m)}
              style={{
                flex: 1,
                minWidth: 66,
                padding: '10px 6px',
                borderRadius: 'var(--r-sm)',
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${
                  uiMethod === m ? 'var(--accent)' : 'var(--rule-strong)'
                }`,
                background: uiMethod === m ? 'var(--accent-wash)' : '#fff',
                color: uiMethod === m ? 'var(--accent-deep)' : 'var(--ink-soft)',
              }}
            >
              {methodLabel(m)}
            </button>
          ))}
        </div>
      </div>

      {/* PayPal Tier B (USD) — generate an auto-reconciling pay link. Shown
          only for a USD PayPal receipt. The link clears the gate server-side
          when the client pays; recording here is optional (the capture does
          it). The separate full Payment-Details-Sender composer is future. */}
      {uiMethod === 'paypal' && (
        <div style={fxBox}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: USD_ACCENT }}>
            PayPal link (USD · auto-reconciles)
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
            Generates a PayPal pay link for <b>{usd(amount)}</b> against this
            gate. Send it to the client — when they pay, the money-gate is
            released automatically (no manual mark-paid needed).
          </div>
          <button
            type="button"
            onClick={genPaypalLink}
            disabled={ppBusy || !(amount > 0)}
            style={{
              alignSelf: 'flex-start',
              padding: '9px 14px',
              borderRadius: 'var(--r-sm)',
              fontSize: 12.5,
              fontWeight: 700,
              cursor: ppBusy || !(amount > 0) ? 'not-allowed' : 'pointer',
              border: `1px solid ${USD_ACCENT}`,
              background: ppBusy || !(amount > 0) ? '#eef2f6' : '#fff',
              color: USD_ACCENT,
            }}
          >
            {ppBusy ? 'Creating link…' : 'Generate PayPal link (USD)'}
          </button>
          {ppErr && <div style={errBox}>{ppErr}</div>}
          {ppLink && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={lbl}>Approve link — copy &amp; send to the client</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  readOnly
                  value={ppLink}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{ ...input, flex: 1, fontSize: 12 }}
                />
                <button
                  type="button"
                  onClick={copyPaypalLink}
                  style={{
                    padding: '0 14px',
                    borderRadius: 'var(--r-sm)',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: `1px solid ${USD_ACCENT}`,
                    background: USD_ACCENT,
                    color: '#fff',
                  }}
                >
                  {ppCopied ? 'Copied ✓' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* reference + note */}
      <div>
        <label style={lbl}>
          Reference / txn id{' '}
          <span style={{ fontWeight: 400, color: 'var(--ink-mute)' }}>
            (optional)
          </span>
        </label>
        <input
          style={input}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="e.g. UTR / wire ref / cheque no."
        />
      </div>
      <div>
        <label style={lbl}>
          Note{' '}
          <span style={{ fontWeight: 400, color: 'var(--ink-mute)' }}>
            (optional, internal)
          </span>
        </label>
        <textarea
          style={{ ...input, minHeight: 56, resize: 'vertical' }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. bank credit confirmed…"
        />
      </div>

      {/* receipt attach (stub — Storage wiring is a later slice) */}
      <div style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
        Receipt / proof attach lands with the invoice slice (Storage-gated).
      </div>

      {/* consequence */}
      {plan && !plan.error && (
        <div
          style={{
            ...conseq,
            background: gate.overrideOpen
              ? '#f0ecf8'
              : plan.clearsGate
                ? 'var(--good-wash)'
                : 'var(--warn-wash)',
            borderColor: gate.overrideOpen
              ? '#d8cdee'
              : plan.clearsGate
                ? 'var(--accent-soft)'
                : 'var(--warn)',
            color: gate.overrideOpen
              ? '#6b4ea3'
              : plan.clearsGate
                ? 'var(--accent-deep)'
                : 'var(--warn)',
          }}
        >
          {plan.consequence}
        </div>
      )}
      {plan?.error && <div style={errBox}>{plan.error}</div>}
    </div>
  );
}

function Row({
  l,
  v,
  good,
  highlight,
}: {
  l: string;
  v: string;
  good?: boolean;
  highlight?: 'good' | 'warn';
}) {
  const bg =
    highlight === 'good'
      ? 'var(--good-wash)'
      : highlight === 'warn'
        ? 'var(--warn-wash)'
        : 'transparent';
  const fg =
    highlight === 'good'
      ? 'var(--good)'
      : highlight === 'warn'
        ? 'var(--warn)'
        : good
          ? 'var(--good)'
          : 'var(--ink)';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '9px 13px',
        fontSize: 12.5,
        borderBottom: '1px solid var(--rule)',
        background: bg,
      }}
    >
      <span style={{ color: 'var(--ink-soft)', fontWeight: highlight ? 600 : 400 }}>
        {l}
      </span>
      <span style={{ fontWeight: 700, color: fg, fontVariantNumeric: 'tabular-nums' }}>
        {v}
      </span>
    </div>
  );
}

/* ════════════════════ Step 3 — Done ════════════════════ */

function DoneStep({
  project,
  gate,
  amount,
  result,
  fmt,
}: {
  project: LiveProject;
  gate: PayableGate;
  amount: number;
  result: SubmitResult;
  fmt: (n: number) => string;
}) {
  const cleared = result.cleared;
  const s = result.settlement;
  const isUsd = s.currencyReceived === 'USD';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'var(--accent)',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 26,
          }}
        >
          ✓
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, marginTop: 12 }}>
          Payment recorded · {fmt(amount)}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 5 }}>
          {result.plan.settlesOverride
            ? cleared
              ? 'Override settled — chasing reminder stopped. Nothing re-released.'
              : 'Override partially settled — the reminder continues.'
            : cleared
              ? `${gate.label} cleared — the deliverable is released to ${project.clientName}.`
              : `Part-payment recorded — the gate stays open (${fmt(
                  result.plan.balanceAfter,
                )} remaining).`}
        </div>
      </div>

      {/* recap */}
      <div style={recap}>
        <RecapRow k="Client · project" v={`${project.clientName} · ${project.vp}`} />
        <RecapRow
          k={isUsd ? 'Received (USD)' : 'Amount'}
          v={fmt(amount)}
        />
        {isUsd && (
          <>
            <RecapRow
              k="INR credited (bank)"
              v={s.settledINR != null ? inr(s.settledINR) : 'awaiting credit'}
            />
            <RecapRow
              k="Effective rate"
              v={s.fxRate != null ? `₹${s.fxRate.toFixed(2)} / $` : '—'}
            />
          </>
        )}
        <RecapRow
          k="Gate"
          v={cleared ? `${gate.label} · cleared` : `${gate.label} · partly paid`}
        />
        <RecapRow
          k="Deliverable"
          v={
            result.plan.settlesOverride
              ? 'already released (override)'
              : cleared
                ? 'released to client ✓'
                : 'held until balance clears'
          }
          hl={cleared && !result.plan.settlesOverride}
        />
      </div>

      {/* Receipt sending is NOT wired yet — no send controls (owner hard rule:
          no dead controls). The muted line below is the whole story. */}
      <div style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
        Receipt sending (WhatsApp / email) arrives in a later update — receipts are
        manual for now. The client already sees this payment on their own statement
        automatically.
      </div>
    </div>
  );
}

function RecapRow({ k, v, hl }: { k: string; v: string; hl?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 10,
        padding: '11px 14px',
        fontSize: 13,
        borderBottom: '1px solid var(--rule)',
        background: hl ? 'var(--good-wash)' : 'transparent',
      }}
    >
      <span style={{ color: 'var(--ink-soft)' }}>{k}</span>
      <span style={{ fontWeight: 600, color: hl ? 'var(--good)' : 'var(--ink)' }}>
        {v}
      </span>
    </div>
  );
}

/* ════════════════════ Shared bits ════════════════════ */

function CurChip({ currency }: { currency: 'INR' | 'USD' }) {
  const usd = currency === 'USD';
  return (
    <span
      style={{
        ...pill,
        background: usd ? '#e9f0f7' : '#fff',
        color: usd ? USD_ACCENT : 'var(--ink-soft)',
        border: `1px solid ${usd ? '#c4d6e8' : 'var(--rule-strong)'}`,
      }}
    >
      {usd ? '$' : '₹'} {currency}
    </span>
  );
}

/* ════════════════════ styles ════════════════════ */

const ovl: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(20,20,30,.42)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '4vh 16px',
  zIndex: 200,
  overflowY: 'auto',
};
const sheet: React.CSSProperties = {
  background: '#fff',
  borderRadius: 'var(--r-lg)',
  width: 'min(560px, 100%)',
  boxShadow: '0 24px 60px rgba(20,20,30,.4)',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '92vh',
};
const head: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid var(--rule)',
};
const xbtn: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--rule)',
  background: '#fff',
  cursor: 'pointer',
  color: 'var(--ink-soft)',
};
const stepWrap: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '14px 20px 0',
};
const body: React.CSSProperties = {
  padding: 20,
  overflowY: 'auto',
  flex: 1,
};
const foot: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 10,
  padding: '14px 20px',
  borderTop: '1px solid var(--rule)',
  background: 'var(--inset)',
};
const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ink-soft)',
  marginBottom: 6,
};
const input: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '10px 12px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: '#fff',
  boxSizing: 'border-box',
};
const amtCtrl: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 12px',
};
const amtInput: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: 20,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  width: '100%',
};
const pickerList: React.CSSProperties = {
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  marginTop: 8,
  maxHeight: 200,
  overflowY: 'auto',
};
const popt: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '10px 12px',
  borderBottom: '1px solid var(--rule)',
  background: '#fff',
  cursor: 'pointer',
  textAlign: 'left',
};
const poptOn: React.CSSProperties = { background: 'var(--accent-wash)' };
const pill: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 'var(--r-pill)',
  marginLeft: 'auto',
  whiteSpace: 'nowrap',
};
const chosen: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  background: 'var(--inset)',
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '9px 12px',
  fontSize: 12.5,
};
const gateBox: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 14px',
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
};
const fxBox: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '13px 14px',
  border: '1px solid #c4d6e8',
  background: '#f4f8fc',
  borderRadius: 'var(--r-sm)',
};
const seg: React.CSSProperties = {
  display: 'flex',
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  overflow: 'hidden',
};
const segBtn: React.CSSProperties = {
  flex: 1,
  padding: '9px 8px',
  fontSize: 12.5,
  fontWeight: 600,
  background: '#fff',
  border: 'none',
  borderRight: '1px solid var(--rule)',
  color: 'var(--ink-soft)',
  cursor: 'pointer',
};
const segOn: React.CSSProperties = { background: 'var(--ink)', color: '#fff' };
const balPrev: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  overflow: 'hidden',
};
const conseq: React.CSSProperties = {
  border: '1px solid',
  borderRadius: 'var(--r-sm)',
  padding: '12px 14px',
  fontSize: 12.5,
  lineHeight: 1.5,
};
const recap: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-md)',
  overflow: 'hidden',
};
const noteBox: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: 14,
  fontSize: 12.5,
  color: 'var(--ink-soft)',
  background: 'var(--inset)',
};
const errBox: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 'var(--r-sm)',
  background: '#fbeae7',
  border: '1px solid #e3b9b1',
  color: '#a83b2c',
  fontSize: 12.5,
};
